import type { WindowLevelRange } from '@/modules/Editor/MedicalMetadata'
import type { WindowLevels } from '@/modules/Editor/imageManipulation'
import {
  MAX_PIXEL_CHANNEL_256,
  MAX_PIXEL_CHANNEL_64k,
  MIN_PIXEL_CHANNEL,
  NUM_PIXEL_CHANNELS_256,
  NUM_PIXEL_CHANNELS_64k,
} from '@/modules/Editor/imageManipulation'
import { backendWindowLevelRangeToPixelRange } from './radiology/scaleWindowLevelRange'
import type { View } from '@/modules/Editor/views/view'

type ColorSpace = 'RGB' | 'RG16'

export const getNumPixelChannels = (colorspace?: ColorSpace | null): number =>
  colorspace === 'RG16' ? NUM_PIXEL_CHANNELS_64k : NUM_PIXEL_CHANNELS_256

/**
 * For N channels, the max value is N-1, as the first channel is intepretted as 0.
 */
export const getMaxWindowLevel = (colorspace?: ColorSpace | null): number =>
  colorspace === 'RG16' ? MAX_PIXEL_CHANNEL_64k : MAX_PIXEL_CHANNEL_256

/**
 * Returns default for colorspace window/level range or
 * formats provided window level
 *
 * @param colorspace
 * @param defaultWindow
 * @param allowNegativeMin - When true, allows minimum to go below zero (default: false)
 * @returns {[number, number]}
 */
export const getWindowLevelsRange = (
  colorspace?: ColorSpace | null,
  defaultWindowLevelRange?: WindowLevelRange | null,
): WindowLevels => {
  let maxLevel = getMaxWindowLevel(colorspace)
  let minLevel = MIN_PIXEL_CHANNEL

  if (defaultWindowLevelRange) {
    const { min, max } = defaultWindowLevelRange
    if (minLevel < min) {
      minLevel = min
    }
    if (maxLevel > max) {
      maxLevel = max
    }
  }

  return [minLevel, maxLevel]
}

export const refineWindowLevels = (value: number, colorspace?: ColorSpace | null): number => {
  const range = getWindowLevelsRange(colorspace)
  return Math.max(range[0], Math.min(range[1], value))
}

export const windowFunction = (v: number, low: number, high: number): number => {
  if (v <= low) {
    return 0
  }
  if (v >= high) {
    return 255
  }
  return Math.floor(((v - low) / (high - low)) * 255.0)
}

/**
 * Gets the correct window levels for rendering, handling backend window levels conversion.
 * Backend window levels are in Hounsfield units and need to be converted to PNG pixel values
 * for the rendering pipeline to work correctly.
 *
 * @param view The view instance
 * @returns Window levels in PNG pixel values suitable for rendering
 */
export const getWindowLevelsForRendering = (view: View): WindowLevels => {
  const backendWindowLevels = view.editor.backendWindowLevels
  const defaultWindowLevels = view.defaultWindowLevels

  if (!backendWindowLevels) {
    return defaultWindowLevels
  }

  // For non-DICOM or non-medical files, use backend values directly
  const medicalMetadata = view.fileManager.metadata?.medical
  if (!medicalMetadata) {
    return backendWindowLevels
  }

  // Convert backend window levels from Hounsfield units to PNG pixel values
  const numPixelChannels = getNumPixelChannels(view.fileManager.metadata?.colorspace)
  return backendWindowLevelRangeToPixelRange(backendWindowLevels, numPixelChannels, medicalMetadata)
}
